// src/pages/outsourcing_management/InquiryEditForm.tsx
//
// Editing an inquiry, in the dialog that shows it - the way
// RequisitionEditableFieldsForm sits inside RequisitionDetailDialog. Before
// this, the Edit button closed the detail dialog and opened InquiryComponent,
// the create form wearing a second shape for the same record. Now the facts
// grid swaps for this form in place and the create half is its sibling, both
// rendering the same InquiryFields and the same InquiryItemsTable.
//
// What is editable depends on status, and matches what the backend enforces:
// a Draft has not been shown to anyone, so its title and type are still open;
// past Draft they are frozen and update_inquiry 409s the change. Everything
// else stays editable at every stage - a supplier can be chosen late, and the
// EE-240 partner-history fields are filled in as the supplier answers.
//
// The item lines follow the same Draft-only rule, though nothing on the server
// enforces that one: an inquiry that has been sent is a question a supplier is
// already answering, and changing what was asked for underneath them is not an
// edit, it is a new inquiry. So the lines are editable while the inquiry is a
// Draft and read-only after it, alongside the title and the type.
//
// This form owns the lines while it is open, so one Save writes the fields and
// the lines together. It also has to send the whole set: update_inquiry
// deletes any existing line whose product is absent from the list it is given,
// which is how the old form lost lines - it sent `items: selectedItems`, which
// holds only what came from an order or a quote, so saving an inquiry that
// also carried custom or requisition-born lines deleted every one of them.
// `items` is omitted entirely when the lines were not touched.
import {
  FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import {
  apiAddInquiryCandidate,
  apiRemoveInquiryCandidate,
  apiSetInquiryCandidateSendEmail,
  apiUpdateInquiry,
} from "util/network/Inquiries";
import { usePartners } from "hooks/usePartners";
import {
  CreateInquiryItemType,
  InquiryPartnerCandidateType,
  InquiryStatusEnum,
  InquiryDetailType,
  UpdateInquiryType,
} from "types/InquiryTypes";
import { ProductTypeEnum } from "types/products/ProductCommandsType";
import { isInquiryOrdered } from "./constants/inquiryStatusColors";
import {
  customItemToInquiryItem,
  useInquiryItems,
} from "components/Inquiry/hooks/useInquiryItems";
import InquiryFields from "./InquiryFields";
import InquiryPartnerShortlist from "./InquiryPartnerShortlist";
import {
  compareSupplierNames,
  SupplierAutocomplete,
} from "components/Dashboard/supplierOption";
import InquiryItemsTable from "components/Inquiry/items/InquiryItemsTable";
import { InquiryDraft } from "components/Inquiry/DraftEmailPreview";
import { lineFromInquiryItem } from "components/Inquiry/items/inquiryLine";
import { ItemLinesScroll } from "./InquiryDialogLayout";
import {
  InquiryFormValues,
  customItemsFromInquiry,
  initialEditValues,
  selectedFromInquiry,
  useInquiryProdObj,
} from "./inquiryFormValues";

type PropsType = {
  inquiry: InquiryDetailType;
  onSaved: () => void;
  onCancel: () => void;
  /**
   * Reports what the form currently holds, so the column beside it can preview
   * the email that is being written rather than the one that is stored.
   */
  onDraftChange?: (draft: InquiryDraft) => void;
  /**
   * Re-reads the inquiry. The shortlist writes straight to its own endpoints
   * rather than through Save - a candidate is a row with its own send state,
   * not a form value - so the record has to be picked up again after one
   * changes.
   */
  onChanged: () => void;
};

const CLEAR_UNSUPPORTED = "Clearing this field isn't supported yet.";

// update_inquiry guards its item block with `if changes.items:`, so an empty
// list reads as "no change" and the last line cannot be removed through it.
const NO_LINES = "An inquiry needs at least one line.";

// Lines are matched on product_id, not on the line's own id - two lines
// carrying the same product collapse into one, the second write overwriting
// the first. Every free-text line gets a product of its own, so this only ever
// fires on a duplicated order or quote line.
const DUPLICATE_PRODUCT =
  "Two lines carry the same product. Remove one of them - the inquiry can only hold each product once.";

/** The signature of a set of lines, for telling "touched" from "not touched". */
const signature = (items: CreateInquiryItemType[]) =>
  items
    .map((item) => `${item.productId}:${item.quantity}`)
    .sort()
    .join("|");

const InquiryEditForm: FunctionComponent<PropsType> = ({
  inquiry,
  onSaved,
  onCancel,
  onDraftChange,
  onChanged,
}) => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();

  const isDraft = inquiry.state === InquiryStatusEnum.DRAFT;
  // Before Ordered the form edits the shortlist; from Ordered on, only the
  // supplier it was ordered from - see isInquiryOrdered.
  const ordered = isInquiryOrdered(inquiry);

  const initial = useMemo(() => initialEditValues(inquiry), [inquiry]);
  const [values, setValues] = useState(initial);
  const [saving, setSaving] = useState(false);

  const patch = (changes: Partial<InquiryFormValues>) =>
    setValues((current) => ({ ...current, ...changes }));

  const isDirty = (Object.keys(initial) as (keyof InquiryFormValues)[]).some(
    (key) => values[key] !== initial[key]
  );

  // ── The item lines ─────────────────────────────────────────────────────────
  const prodType: "order" | "cart" = inquiry.quoteId ? "cart" : "order";
  const { prodObj, isError: prodObjFailed } = useInquiryProdObj(inquiry);
  const initialSelected = useMemo(() => selectedFromInquiry(inquiry), [inquiry]);
  const initialCustom = useMemo(
    () => customItemsFromInquiry(inquiry),
    [inquiry]
  );
  const items = useInquiryItems(
    prodObj,
    values.type,
    prodType,
    initialSelected,
    initialCustom
  );

  // Mirrors what useInquiryItems merges, custom lines and all, so an untouched
  // inquiry does not read as dirty on the strength of a formatting difference.
  const initialMerged = useMemo<CreateInquiryItemType[]>(
    () => [...initialSelected, ...initialCustom.map(customItemToInquiryItem)],
    [initialSelected, initialCustom]
  );

  // Every keystroke reaches the preview from here. The hook behind it
  // debounces the render call, so this is a state update per change, not a
  // request per change.
  useEffect(() => {
    onDraftChange?.({
      values,
      mergedItems: items.mergedItems,
      prodObj,
      prodType,
    });
    // onDraftChange is a setState from the dialog above; listing it would
    // re-run this on every render of that dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, items.mergedItems, prodObj, prodType]);

  // A product-less line (EE-207, converted from a raw-material requisition)
  // has no productId to send, and any items-bearing save would delete it. The
  // c7f1a2b93d40 migration back-filled these and made product_id NOT NULL, so
  // this should be dead - it is cheap insurance against silently losing one.
  const hasProductlessLine = inquiry.items.some((item) => !item.product);
  // Lines that came from an order or a quote can only be listed once that
  // order or quote is in hand.
  const needsProdObj = inquiry.items.some(
    (item) => item.product && item.product.productType !== ProductTypeEnum.OTHER
  );
  // Lines built from requisition lines (EE-253 P2b) change through the
  // compose dialog, which keeps the allocations in step; the product picker
  // here would rewrite them by product and lose which requisition line each
  // one carries.
  const pooled = inquiry.items.some((item) => (item.sources ?? []).length > 0);
  const itemsEditable =
    isDraft &&
    !pooled &&
    !hasProductlessLine &&
    (!needsProdObj || prodObj !== undefined);

  const itemsDirty =
    itemsEditable && signature(items.mergedItems) !== signature(initialMerged);

  // update_inquiry assigns req_item.comment from the payload unconditionally,
  // and the item editor has no comment of its own to put there, so any
  // items-bearing save blanked INQUIRYITEMS.comment on every line it touched.
  // That column is where a requisition-born line keeps its material spec
  // (ensure_enquiry_for_requisition writes it), so the loss was silent and
  // permanent. Carry each line's stored comment back out with it.
  const commentByProduct = useMemo(() => {
    const comments = new Map<number, string>();
    inquiry.items.forEach((item) => {
      if (item.product && item.comment) comments.set(item.product.id, item.comment);
    });
    return comments;
  }, [inquiry]);

  // A line the inquiry carries but its own type filters out of the picker: it
  // is invisible, cannot be unticked, and still travels in the payload. Only
  // reachable straight after loading such an inquiry - a type change from here
  // re-filters the selection (REFILTER), which clears the mismatch.
  const hiddenSelectedCount = items.selectedItems.filter(
    (selected) =>
      !items.prodItems.some((prod) => prod.product.id === selected.productId)
  ).length;

  // Only worth complaining about lines that are actually being sent: an
  // inquiry that already had no lines can still have a note corrected.
  let itemError: string | undefined;
  if (itemsDirty && items.mergedItems.length === 0) itemError = NO_LINES;
  else if (
    itemsDirty &&
    new Set(items.mergedItems.map((item) => item.productId)).size !==
      items.mergedItems.length
  )
    itemError = DUPLICATE_PRODUCT;

  // update_inquiry assigns message / deliveryMethod / deadline only when they
  // arrive truthy, so emptying one reports success and then snaps back on the
  // next read. Say so rather than pretend it saved.
  // The shortlist as the form holds it. Edits are collected here and written
  // on Save with everything else, so Cancel cancels them - they used to hit
  // their own endpoints on click, which no Cancel could undo. A row that has
  // not been saved yet carries a negative id; the diff in handleSave reads
  // that as "add this one".
  const partners = usePartners() ?? [];
  const original = useMemo(
    () => inquiry.partnerCandidates ?? [],
    [inquiry.partnerCandidates]
  );
  const [candidates, setCandidates] =
    useState<InquiryPartnerCandidateType[]>(original);
  const nextTempId = useRef(-1);

  // Re-seed when the server's shortlist actually changes underneath the form -
  // a Save that failed part-way, or a workflow action in the panel beside
  // this one. Keyed on the content rather than on the array, which is a new
  // object on every re-read of the inquiry and would otherwise throw away
  // edits in progress for no reason.
  const originalSignature = useMemo(
    () => original.map((c) => `${c.id}:${c.send_email}`).join("|"),
    [original]
  );
  // The same signature over what is on screen. Without this the shortlist was
  // the one part of the form that could not make Save Changes light up:
  // isDirty walks the keys of InquiryFormValues, and the candidates are their
  // own state. Adding a supplier or flipping a toggle left the button
  // disabled, saveShortlist never ran, and the edit was gone on close.
  //
  // Removing the settled supplier looked like it worked only because it also
  // clears partnerId, which is a form value - hence "sometimes saves".
  const candidatesSignature = useMemo(
    () => candidates.map((c) => `${c.id}:${c.send_email}`).join("|"),
    [candidates]
  );
  const candidatesDirty = candidatesSignature !== originalSignature;
  const seeded = useRef(originalSignature);
  useEffect(() => {
    if (seeded.current === originalSignature) return;
    seeded.current = originalSignature;
    setCandidates(original);
  }, [originalSignature, original]);

  const addCandidate = (partnerId: number, sendEmail: boolean) => {
    const partner = partners.find((p) => p.id === partnerId);
    setCandidates((current) => [
      ...current,
      {
        id: nextTempId.current--,
        partner_id: partnerId,
        partner_name: partner?.info?.company ?? null,
        partner_email: partner?.info?.email ?? null,
        requisition_id: null,
        inquiry_id: inquiry.id,
        sent_at: null,
        send_email: sendEmail,
        is_final: false,
      },
    ]);
  };

  const removeCandidate = (candidateId: number) => {
    const dropped = candidates.find((c) => c.id === candidateId);
    setCandidates((current) => current.filter((c) => c.id !== candidateId));
    // The winner cannot be somebody who is no longer being asked.
    if (dropped && values.partnerId === dropped.partner_id) {
      patch({ partnerId: undefined });
    }
  };

  const setCandidateSendEmail = (candidateId: number, sendEmail: boolean) =>
    setCandidates((current) =>
      current.map((c) => (c.id === candidateId ? { ...c, send_email: sendEmail } : c))
    );

  // Applied in one pass at Save. Each is its own endpoint - there is no
  // batch - so this is a sequence of calls rather than one transaction, and
  // a failure part-way is reported as exactly that.
  const saveShortlist = async () => {
    for (const candidate of original) {
      if (!candidates.some((c) => c.id === candidate.id)) {
        await apiRemoveInquiryCandidate(token, inquiry.id, candidate.id);
      }
    }
    for (const candidate of candidates) {
      if (candidate.id < 0) {
        await apiAddInquiryCandidate(
          token,
          inquiry.id,
          candidate.partner_id,
          candidate.send_email
        );
        continue;
      }
      const before = original.find((o) => o.id === candidate.id);
      if (before && before.send_email !== candidate.send_email) {
        await apiSetInquiryCandidateSendEmail(
          token,
          inquiry.id,
          candidate.id,
          candidate.send_email
        );
      }
    }
  };

  const errors: Partial<Record<keyof InquiryFormValues, string>> = {};
  if (inquiry.message && !values.message) errors.message = CLEAR_UNSUPPORTED;
  if (inquiry.deliveryMethod && !values.deliveryMethod)
    errors.deliveryMethod = CLEAR_UNSUPPORTED;
  if (initial.deadline && !values.deadline) errors.deadline = CLEAR_UNSUPPORTED;
  const hasUnsupportedClear = Object.keys(errors).length > 0;

  const handleSave = () => {
    const changes: UpdateInquiryType = {
      // The input model requires a state; this form never changes one. Status
      // is a workflow action, in the panel to the right.
      state: inquiry.state,
      // null, not undefined: undefined is "leave it", null is "clear it".
      partnerId: values.partnerId ?? null,
      // Only a Draft may change these and the backend 409s if one tries, so
      // they are sent only while the form actually let them be edited.
      title: isDraft ? values.title : undefined,
      type: isDraft ? values.type : undefined,
      deadline: values.deadline || undefined,
      deliveryMethod: values.deliveryMethod,
      message: values.message,
      // EE-240: always sent, so clearing one clears it server-side too.
      deliveryTime: values.deliveryTime,
      notes: values.notes,
      // The whole set, or nothing at all. See the note at the top.
      items: itemsDirty
        ? items.mergedItems.map((item) => ({
            ...item,
            comment: commentByProduct.get(item.productId),
          }))
        : undefined,
    };
    setSaving(true);
    // The shortlist first: "Ordered from" may name a supplier who is only
    // being added now, and the fields are the half worth keeping if one of
    // the two halves has to fail.
    saveShortlist()
      .then(() =>
        // sendEmail=false: saving is bookkeeping. Sending is Send again,
        // which is its own button on the saved inquiry.
        apiUpdateInquiry(token, inquiry.id, changes, false, undefined)
      )
      .then(() => {
        addToast({ type: "success", message: "Inquiry updated" });
        onSaved();
      })
      .catch((err) => {
        addToast({
          type: "error",
          message: `Could not update the inquiry: ${err}`,
          keep: true,
        });
        // A shortlist edit may have gone through before the failure, so the
        // form is re-seeded from the server rather than left showing a set
        // that no longer matches it.
        onChanged();
      })
      .finally(() => setSaving(false));
  };

  return (
    <div style={{ marginBottom: "1rem" }}>
      <InquiryFields
        values={values}
        onChange={patch}
        lockCoreFields={!isDraft}
        errors={errors}
        supplierSlot={
          <>
            <Divider sx={{ my: 1.5 }} />
            {/* Who is being asked. The shortlist is the flow now - one
                inquiry out to several suppliers - and this is the only place
                it can be changed: the read view shows it, editing edits it.

                Edits are held in form state and written on Save with
                everything else, so Cancel discards them. A supplier who has
                already been sent the inquiry cannot be removed at all - the
                backend refuses it, and the buttons say so. */}
            {!ordered && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  Potential suppliers
                </Typography>
                <InquiryPartnerShortlist
                  candidates={candidates}
                  editable
                  serviceType={values.type}
                  onAdd={addCandidate}
                  onRemove={removeCandidate}
                  onSetSendEmail={setCandidateSendEmail}
                />
                <Divider sx={{ my: 1.5 }} />
              </>
            )}
            {/* And which of them it was ordered from. Constrained to the
                shortlist because _require_settled_partner refuses a winner
                who was never asked - offering every partner in the system
                here would just build a request the server rejects. An
                inquiry with no shortlist predates candidates entirely, so
                that one keeps the open picker. Only from Ordered on: before
                that the Order action asks for the winner itself. */}
            {!ordered ? null : candidates.length > 0 ? (
              <TextField
                select
                fullWidth
                size="small"
                label="Ordered from"
                value={values.partnerId ?? ""}
                onChange={(e) =>
                  patch({
                    partnerId: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
                helperText="Picked from the shortlist the inquiry went out to"
              >
                <MenuItem value="">
                  <em>Not chosen yet</em>
                </MenuItem>
                {[...candidates]
                  .sort((a, b) =>
                    compareSupplierNames(
                      a.partner_name ?? `Partner #${a.partner_id}`,
                      b.partner_name ?? `Partner #${b.partner_id}`
                    )
                  )
                  .map((candidate) => (
                    <MenuItem
                      key={candidate.id}
                      value={candidate.partner_id}
                    >
                      {candidate.partner_name ??
                        `Partner #${candidate.partner_id}`}
                    </MenuItem>
                  ))}
              </TextField>
            ) : (
              <SupplierAutocomplete
                value={values.partnerId}
                onChange={(partnerId) => patch({ partnerId })}
                serviceType={values.type}
              />
            )}
          </>
        }
      />

      {/* The lines live inside the form rather than beside it, so that Save
          covers everything above it. InquiryDialog draws the read-only table
          in this spot when the form is closed. */}
      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Item lines
      </Typography>
      {itemsEditable ? (
        <>
          <ItemLinesScroll>
            <InquiryItemsTable
              mode="edit"
              inquiryType={values.type}
              prodItems={items.prodItems}
              selectedItems={items.selectedItems}
              customItems={items.customItems}
              prodType={prodType}
              toggle={items.toggle}
              selectAll={items.selectAll}
              deselectAll={items.deselectAll}
              addCustomItem={items.addCustomItem}
              updateCustomItem={items.updateCustomItem}
              removeCustomItem={items.removeCustomItem}
            />
          </ItemLinesScroll>
          {itemError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
              {itemError}
            </Typography>
          )}
          {hiddenSelectedCount > 0 && (
            <Typography
              variant="caption"
              color="warning.main"
              display="block"
              sx={{ mt: 1 }}
            >
              {hiddenSelectedCount === 1
                ? "One line on this inquiry is not of the inquiry's type, so it is not listed above and cannot be changed here. It stays on the inquiry as it is."
                : `${hiddenSelectedCount} lines on this inquiry are not of the inquiry's type, so they are not listed above and cannot be changed here. They stay on the inquiry as they are.`}
            </Typography>
          )}
        </>
      ) : (
        <>
          {inquiry.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No lines on this inquiry.
            </Typography>
          ) : (
            <ItemLinesScroll>
              <InquiryItemsTable
                mode="read"
                lines={inquiry.items.map(lineFromInquiryItem)}
                inquiryType={inquiry.type}
              />
            </ItemLinesScroll>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            {!isDraft
              ? "Lines are fixed once the inquiry leaves Draft - the supplier has already been asked for these."
              : pooled
              ? "These lines come from requisitions - change them with Edit requisition lines below."
              : hasProductlessLine
              ? "This inquiry has a line with no product behind it, which cannot be edited here yet."
              : prodObjFailed
              ? `The ${prodType === "cart" ? "quote" : "order"} behind these lines could not be loaded, so they cannot be edited. Close the dialog and try again.`
              : "Loading the order behind these lines..."}
          </Typography>
        </>
      )}

      <Grid
        container
        sx={{
          mt: 2,
          display: "flex",
          // Only the buttons are left on this row now that the send checkbox
          // is gone, so they sit at the end of it rather than at the start.
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {/* Saving never emails. The old form had a switch for it, on by
            default, so every edit re-sent the inquiry; there is no switch at
            all now. An edit worth telling the supplier about is Send again on
            the saved inquiry - see InquiryWorkflowActions. */}
        <span style={{ display: "flex", gap: "0.5rem" }}>
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              (!isDirty && !itemsDirty && !candidatesDirty) ||
              hasUnsupportedClear ||
              itemError !== undefined ||
              saving
            }
            sx={{
              background: "var(--mainYellow)",
              "&:hover": {
                color: "var(--mainYellow)",
                border: "1px solid var(--mainYellow)",
                background: "transparent",
              },
            }}
          >
            {saving ? <CircularProgress size={18} /> : "Save changes"}
          </Button>
        </span>
      </Grid>
    </div>
  );
};

export default InquiryEditForm;
